import * as yup from 'yup'
import { PermissionObjectType, PermissionType } from '~/common/generated/apollo-operations'

/** Permission schemas based on the actual permission model */
export const generalTablePermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup
    .string()
    .oneOf([
      PermissionType.ADD_ROW,
      PermissionType.DELETE_ROW,
      PermissionType.CONFIGURE_TABLE_TABS,
      PermissionType.CONFIGURE_PIVOT_TABLES,
    ])
    .required(),
  objectType: yup.string().oneOf([PermissionObjectType.TABLE]).required(),
  table: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
    .required(),
})

export const fullTablePermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup
    .string()
    .oneOf([PermissionType.VIEW_ALL_TABLE_CELLS, PermissionType.EDIT_ALL_TABLE_CELLS])
    .required(),
  objectType: yup.string().oneOf([PermissionObjectType.TABLE]).required(),
  table: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
    .required(),
})

/** Tab row visibility only — configure tabs is a table-level permission (see generalTablePermissionSchema). */
export const tableTabPermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup.string().oneOf([PermissionType.VIEW_TABLE_TAB_ROWS]).required(),
  objectType: yup.string().oneOf([PermissionObjectType.TABLE_TAB]).required(),
  tableTab: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
      table: yup
        .object()
        .shape({
          id: yup.string().required(),
        })
        .required(),
    })
    .required(),
})

export const tableColumnPermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup
    .string()
    .oneOf([PermissionType.VIEW_COLUMN, PermissionType.EDIT_CELL])
    .required(),
  objectType: yup.string().oneOf([PermissionObjectType.TABLE_COLUMN]).required(),
  tableColumn: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
      table: yup
        .object()
        .shape({
          id: yup.string().required(),
        })
        .required(),
    })
    .required(),
})

export const formPermissionSchema = yup
  .object()
  .shape({
    id: yup.string().required(),
    permissionType: yup.string().oneOf([PermissionType.SUBMIT_FORM]).required(),
    objectType: yup.string().oneOf([PermissionObjectType.FORM]).required(),
    form: yup
      .object()
      .shape({
        id: yup.string().required(),
        name: yup.string().required(),
      })
      .nullable(),
    formV2: yup
      .object()
      .shape({
        id: yup.string().required(),
        name: yup.string().required(),
      })
      .nullable(),
  })
  .test(
    'has-form-or-formV2',
    'Form permission must reference either a V1 form or a V2 form',
    (value) => Boolean(value.form?.id || value.formV2?.id),
  )

export const workspacePermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup
    .string()
    .oneOf([PermissionType.VIEW_WORKSPACE, PermissionType.EDIT_WORKSAPCE])
    .required(),
  objectType: yup.string().oneOf([PermissionObjectType.WORKSPACE]).required(),
  workspace: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
    .required(),
})

export const manualTriggerPermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup.string().oneOf([PermissionType.SUBMIT_MANUAL_TRIGGER]).required(),
  objectType: yup.string().oneOf([PermissionObjectType.MANUAL_TRIGGER]).required(),
  manualTrigger: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
    .required(),
})

export const embedPermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup.string().oneOf([PermissionType.VIEW_EMBED]).required(),
  objectType: yup.string().oneOf([PermissionObjectType.EMBED]).required(),
  embed: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
    .required(),
})

export const endpointPermissionSchema = yup.object().shape({
  id: yup.string().required(),
  permissionType: yup.string().oneOf([PermissionType.CALL_ENDPOINT]).required(),
  objectType: yup.string().oneOf([PermissionObjectType.ENDPOINT]).required(),
  endpoint: yup
    .object()
    .shape({
      id: yup.string().required(),
      slug: yup.string().required(),
    })
    .required(),
})

export type FullTablePermission = yup.InferType<typeof fullTablePermissionSchema>
export type GeneralTablePermission = yup.InferType<typeof generalTablePermissionSchema>
export type TableTabPermission = yup.InferType<typeof tableTabPermissionSchema>
export type TableColumnPermission = yup.InferType<typeof tableColumnPermissionSchema>
export type FormPermission = yup.InferType<typeof formPermissionSchema>
export type WorkspacePermission = yup.InferType<typeof workspacePermissionSchema>
export type ManualTriggerPermission = yup.InferType<typeof manualTriggerPermissionSchema>
export type EmbedPermission = yup.InferType<typeof embedPermissionSchema>
export type EndpointPermission = yup.InferType<typeof endpointPermissionSchema>

export type TablePermission =
  | FullTablePermission
  | TableTabPermission
  | TableColumnPermission
  | GeneralTablePermission

export type ValidPermission =
  | FullTablePermission
  | GeneralTablePermission
  | TableTabPermission
  | TableColumnPermission
  | FormPermission
  | WorkspacePermission
  | ManualTriggerPermission
  | EmbedPermission
  | EndpointPermission

export function isFullTablePermission(permission: unknown): permission is FullTablePermission {
  return fullTablePermissionSchema.isValidSync(permission)
}

export function isGeneralTablePermission(
  permission: unknown,
): permission is GeneralTablePermission {
  return generalTablePermissionSchema.isValidSync(permission)
}

export function isTableTabPermission(permission: unknown): permission is TableTabPermission {
  return tableTabPermissionSchema.isValidSync(permission)
}

export function isTableColumnPermission(permission: unknown): permission is TableColumnPermission {
  return tableColumnPermissionSchema.isValidSync(permission)
}

export function isFormPermission(permission: unknown): permission is FormPermission {
  return formPermissionSchema.isValidSync(permission)
}

export function isWorkspacePermission(permission: unknown): permission is WorkspacePermission {
  return workspacePermissionSchema.isValidSync(permission)
}

export function isManualTriggerPermission(
  permission: unknown,
): permission is ManualTriggerPermission {
  return manualTriggerPermissionSchema.isValidSync(permission)
}

export function isEmbedPermission(permission: unknown): permission is EmbedPermission {
  return embedPermissionSchema.isValidSync(permission)
}

export function isEndpointPermission(permission: unknown): permission is EndpointPermission {
  return endpointPermissionSchema.isValidSync(permission)
}

export function isPermission(permission: unknown): permission is ValidPermission {
  return (
    isFullTablePermission(permission) ||
    isGeneralTablePermission(permission) ||
    isTableTabPermission(permission) ||
    isTableColumnPermission(permission) ||
    isFormPermission(permission) ||
    isWorkspacePermission(permission) ||
    isManualTriggerPermission(permission) ||
    isEmbedPermission(permission) ||
    isEndpointPermission(permission)
  )
}

export function isTablePermission(permission: unknown): permission is TablePermission {
  return (
    isFullTablePermission(permission) ||
    isGeneralTablePermission(permission) ||
    isTableTabPermission(permission) ||
    isTableColumnPermission(permission)
  )
}

export function filterByType<T extends ValidPermission>(
  permissions: unknown[],
  predicate: (permission: unknown) => permission is T,
): T[] {
  return permissions.filter(predicate) as T[]
}
